import React, { useState, useEffect } from 'react';
import { Tag } from '../../types/tag';
import { tagService } from '../../services/tagService';
import { useModalClose } from '../../hooks/useModalClose';

// Simple icons
const X = ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const Plus = ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);

interface TagManagementModalProps {
    isOpen: boolean;
    imageId: string;
    currentTags: Tag[];
    onClose: () => void;
    onTagsUpdated: (tags: Tag[]) => void;
}

const TagManagementModal: React.FC<TagManagementModalProps> = ({
    isOpen,
    imageId,
    currentTags,
    onClose,
    onTagsUpdated
}) => {
    const [availableTags, setAvailableTags] = useState<Tag[]>([]);
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [newTagName, setNewTagName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Use standard modal close behavior (ESC key)
    useModalClose(isOpen, onClose);

    useEffect(() => {
        if (isOpen) {
            loadTags();
            setSelectedTagIds(currentTags.map(t => t.id));
        }
    }, [isOpen, currentTags]);

    const loadTags = async () => {
        try {
            const tags = await tagService.getTags();
            setAvailableTags(tags);
        } catch (error) {
            console.error('Failed to load tags:', error);
        }
    };

    const handleCreateTag = async () => {
        if (!newTagName.trim()) return;

        setIsCreating(true);
        try {
            const newTag = await tagService.createTag({
                name: newTagName.trim(),
                color: getRandomColor()
            });
            setAvailableTags([...availableTags, newTag]);
            setSelectedTagIds([...selectedTagIds, newTag.id]);
            setNewTagName('');
        } catch (error) {
            console.error('Failed to create tag:', error);
        } finally {
            setIsCreating(false);
        }
    };

    const toggleTag = (tagId: number) => {
        if (selectedTagIds.includes(tagId)) {
            setSelectedTagIds(selectedTagIds.filter(id => id !== tagId));
        } else {
            setSelectedTagIds([...selectedTagIds, tagId]);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await tagService.syncTagsForImage(parseInt(imageId.replace('img_', '')), selectedTagIds);
            const updatedTags = availableTags.filter(t => selectedTagIds.includes(t.id));
            onTagsUpdated(updatedTags);
            onClose();
        } catch (error) {
            console.error('Failed to save tags:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const getRandomColor = () => {
        const colors = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899'];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75" onClick={onClose}>
            <div
                className="bg-snapfy-surface rounded-xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto border border-snapfy-border"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-snapfy-text-primary">Gerenciar Tags</h2>
                    <button
                        onClick={onClose}
                        className="text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Create New Tag */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-snapfy-text-secondary mb-2">
                        Criar Nova Tag
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={newTagName}
                            onChange={(e) => setNewTagName(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleCreateTag()}
                            placeholder="Nome da tag..."
                            className="flex-1 bg-snapfy-inner border border-snapfy-border rounded-xl px-3 py-2 text-snapfy-text-secondary focus:ring-2 focus:ring-[#7063f7] focus:outline-none placeholder-snapfy-text-secondary"
                        />
                        <button
                            onClick={handleCreateTag}
                            disabled={!newTagName.trim() || isCreating}
                            className="px-4 py-2 bg-[#7063f7] text-white rounded-xl hover:bg-[#5a4dd4] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            <Plus size={16} />
                            {isCreating ? 'Criando...' : 'Criar'}
                        </button>
                    </div>
                </div>

                {/* Available Tags */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-snapfy-text-secondary mb-2">
                        Selecionar Tags
                    </label>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {availableTags.length === 0 ? (
                            <p className="text-snapfy-text-secondary text-sm text-center py-4">
                                Nenhuma tag disponível. Crie uma nova acima.
                            </p>
                        ) : (
                            availableTags.map(tag => (
                                <label
                                    key={tag.id}
                                    className="flex items-center gap-3 p-2 rounded-xl hover:bg-snapfy-inner cursor-pointer transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedTagIds.includes(tag.id)}
                                        onChange={() => toggleTag(tag.id)}
                                        className="w-4 h-4 text-[#7063f7] bg-snapfy-inner border-snapfy-border rounded focus:ring-[#7063f7] focus:ring-2"
                                    />
                                    <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: tag.color }}
                                    />
                                    <span className="text-snapfy-text-secondary flex-1">{tag.name}</span>
                                </label>
                            ))
                        )}
                    </div>
                </div>

                {/* Selected Tags Preview */}
                {selectedTagIds.length > 0 && (
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-snapfy-text-secondary mb-2">
                            Tags Selecionadas ({selectedTagIds.length})
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {selectedTagIds.map(id => {
                                const tag = availableTags.find(t => t.id === id);
                                if (!tag) return null;
                                return (
                                    <span
                                        key={id}
                                        className="px-3 py-1 rounded-full text-sm font-medium text-white"
                                        style={{ backgroundColor: tag.color }}
                                    >
                                        {tag.name}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-snapfy-inner text-snapfy-text-primary rounded-xl hover:bg-snapfy-inner transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-2 bg-[#7063f7] text-white rounded-xl hover:bg-[#5a4dd4] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {isSaving ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TagManagementModal;
